import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useI18n } from "../i18n";
import { API_BASE_URL, SSO_ENABLED } from "../lib/config";
import { getAuthSession, logout } from "../lib/api";
import { Card } from "./Card";

type SessionState =
  | { status: "loading" }
  | { status: "authenticated"; email?: string }
  /** `expired` distinguishes "you were signed in and no longer are" from "you arrived signed out". */
  | { status: "signed-out"; expired: boolean };

/**
 * The signed-in creator's own address, for the one thing the create page
 * does with it: offering it as where a read receipt should go. Undefined
 * whenever there is nobody to ask — SSO off, or a build that renders the
 * page outside this gate — so every reader of it has to handle "we don't
 * know", and nothing here ever becomes a second source of truth about
 * whether someone is authenticated. `AuthGate` is that, and it renders
 * nothing until the session resolves, so a child mounts with this already
 * settled rather than watching it arrive.
 */
const SessionEmailContext = createContext<string | undefined>(undefined);

/**
 * How a child tells the gate that the *server* has just disowned its
 * session — a 401 on a request the page expected to work. Sessions last
 * hours (SSO_SESSION_TTL_SECONDS), so the common way to meet one that has
 * lapsed is to leave the create page open overnight; without this the page
 * could only show an inline "your session has expired" line, which says
 * what happened but leaves no way to act on it. Calling this puts the
 * sign-in card back, which is the one thing the person can actually do.
 *
 * Null where there is no gate above — SSO off, or the reply composer, whose
 * caller has no account to sign back into. A null here means "handle it
 * yourself", not "ignore it".
 */
const SessionExpiredContext = createContext<(() => void) | null>(null);

export function useSessionEmail(): string | undefined {
  return useContext(SessionEmailContext);
}

export function useSessionExpired(): (() => void) | null {
  return useContext(SessionExpiredContext);
}

function loginUrl(): string {
  const returnTo = `${window.location.pathname}${window.location.search}` || "/";
  return `${API_BASE_URL}/api/auth/saml/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { m, t } = useI18n();
  const [session, setSession] = useState<SessionState>(SSO_ENABLED ? { status: "loading" } : { status: "authenticated" });

  const markExpired = useCallback(() => {
    setSession({ status: "signed-out", expired: true });
  }, []);

  useEffect(() => {
    if (!SSO_ENABLED) return;
    let cancelled = false;
    getAuthSession()
      .then((result) => {
        if (cancelled) return;
        setSession(
          result.authenticated
            ? { status: "authenticated", email: result.email }
            : { status: "signed-out", expired: false },
        );
      })
      .catch(() => {
        if (!cancelled) setSession({ status: "signed-out", expired: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Re-checks the session whenever the tab is brought back to the front.
   * A session outlives most visits but not a night, and the expiry that
   * matters is the one discovered on a page someone has already typed a
   * secret into. Checking on return catches it while the compose box is
   * still empty, which is the difference between "sign in again" and "sign
   * in again, and retype that". A failed check is ignored: a flaky network
   * must not throw someone out of a page they are working in — the 401 on
   * submit is the backstop for that.
   */
  const statusRef = useRef(session.status);
  statusRef.current = session.status;

  useEffect(() => {
    if (!SSO_ENABLED) return;

    function recheck() {
      if (document.visibilityState !== "visible") return;
      if (statusRef.current !== "authenticated") return;
      getAuthSession()
        .then((result) => {
          if (!result.authenticated && statusRef.current === "authenticated") markExpired();
        })
        .catch(() => {
          // Offline, or the API is briefly unreachable. Not evidence of anything.
        });
    }

    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, [markExpired]);

  if (!SSO_ENABLED) {
    return <>{children}</>;
  }

  if (session.status === "loading") {
    return null;
  }

  if (session.status === "signed-out") {
    return (
      <Card narrow animate>
        <div className="heading-block">
          {session.expired && <h1>{m.auth.expired.title}</h1>}
          <p className="muted">{session.expired ? m.auth.expired.body : m.auth.signInRequired}</p>
        </div>
        <a href={loginUrl()}>
          <button type="button" className="btn btn-primary">
            {m.auth.signIn}
          </button>
        </a>
      </Card>
    );
  }

  async function handleSignOut() {
    await logout();
    setSession({ status: "signed-out", expired: false });
  }

  return (
    <>
      <div className="account-bar">
        <span className="muted">{t(m.auth.signedInAs, { email: session.email ?? "" })}</span>
        <button type="button" className="btn btn-secondary" onClick={handleSignOut}>
          {m.auth.signOut}
        </button>
      </div>
      <SessionExpiredContext.Provider value={markExpired}>
        <SessionEmailContext.Provider value={session.email}>{children}</SessionEmailContext.Provider>
      </SessionExpiredContext.Provider>
    </>
  );
}
